import type { Translations } from "./de";

export const en: Translations = {
  weekdaysShort: {
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun",
  },
  common: {
    signIn: "Sign in",
    signOut: "Sign out",
    loading: "Loading…",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    delete: "Delete",
    open: "Open",
    edit: "Edit",
    rename: "Rename",
    create: "Create",
    yes: "Yes",
    no: "No",
    or: "or",
    email: "Email",
    password: "Password",
    language: "Language",
    back: "Back",
    next: "Next",
    close: "Close",
    refresh: "Refresh",
    paginationPrev: "Previous",
    paginationNext: "Next",
    paginationPageOf: "Page {{page}} of {{pages}}",
    paginationShowing: "{{from}}–{{to}} of {{total}}",
    paginationPerPage: "Per page",
  },
  dateField: {
    open: "Choose date",
    placeholder: "Choose date",
    openCalendar: "Open calendar",
    month: "Month",
    year: "Year",
  },
  nav: {
    dashboard: "Dashboard",
    projects: "Projects",
    companies: "Companies",
    admin: "Admin",
  },
  auth: {
    metaTitle: "Sign in — Grantonomy Timesheet AI",
    eyebrow: "Access required",
    title: "Sign in",
    subtitle: "Access your R&D tax credit projects.",
    passwordLabel: "Password",
    passwordSubmit: "Sign in",
    passwordSubmitLoading: "Signing in…",
    magicLink: "Magic link by email",
    magicLinkLoading: "Sending…",
    magicLinkHelp: "Sign in with a password, or use only the email for a magic link.",
    magicLinkSent: "Magic link sent — please check your inbox.",
    magicLinkNoEmail: "Please enter an email",
    forgotPassword: "Forgot your password?",
    resetSent: "If an account exists, an email is on its way.",
    emailPlaceholder: "name@company.com",
    turnstileLabel: "Confirm you are not a robot.",
  },
  footer: {
    navAria: "Navigation",
    home: "Home",
    services: "Services",
    contact: "Contact",
    about: "About",
    projects: "Projects",
    imprint: "Imprint",
    copyright: "© {{year}} Grantonomy. All rights reserved.",
    product: "Grantonomy · Time Tracker",
  },
  setPassword: {
    metaTitle: "Set password — Grantonomy",
    title: "Choose a password.",
    subtitle: "Set a password for your access.",
    resetTitle: "Choose a new password.",
    resetSubtitle: "Reset the password for your access.",
    currentLabel: "Current password",
    label: "New password",
    confirmLabel: "Repeat password",
    hint: "At least {{min_length}} characters.",
    mismatch: "The passwords do not match.",
    submit: "Save password",
    submitting: "Saving…",
    success: "Password saved.",
  },
  linkInvalid: {
    metaTitle: "Link invalid — Grantonomy",
    title: "This link no longer works.",
    expired: "This link has expired. Just request a new one.",
    used: "This link has been used too many times and is no longer valid. Just request a new one.",
    invalid: "This link is not valid. Please request a new one.",
    requestNew: "Request a new link",
    employeeLogin: "Employee login",
  },
  redeem: {
    metaTitle: "Sign in — Grantonomy",
    title: "Almost there.",
    intro: "Click “Sign in” to sign in with your personal link. The link works exactly once.",
    submit: "Sign in",
    submitting: "Signing in…",
  },
  dashboard: {
    metaTitle: "Dashboard — Grantonomy Timesheet AI",
    kicker: "Overview",
    title: "Your R&D projects.",
    newProject: "New project",
    stats: {
      projects: "Projects",
      employees: "Employees",
      timesheets: "Timesheets generated",
    },
    recent: "Recent projects",
    emptyTitle: "No projects yet",
    emptyBody: "Create your first R&D project and enter the master data.",
    emptyCta: "Create your first project",
    renamed: "Project renamed",
    renameFailed: "Failed to rename",
    nameRequired: "Name is required",
    viewCompanies: "Companies",
    viewProjects: "Projects",
    viewsLabel: "Dashboard view",
    openAllCompanies: "All companies",
    openAllProjects: "All projects",
    showMorePage: "Show {{next}} more ({{shown}} of {{total}})",
  },
  companies: {
    metaTitle: "Companies — Grantonomy Timesheet AI",
    kicker: "Customers",
    title: "Companies",
    newCompany: "New company",
    created: "Company created",
    createFailed: "Failed to create",
    emptyTitle: "No companies yet",
    emptyBody:
      "Create a company from New project — pick “Add a new company…” in the company field.",
    formName: "Name",
    formState: "State",
    namePlaceholder: "Acme Ltd.",
  },
  admin: {
    metaTitle: "Admin — Grantonomy Timesheet AI",
    kicker: "Admin",
    title: "Data management",
    subtitle: "Users, entries, email log, week notes and lock/audit log.",
    empty: "No projects yet.",
    deleted: "Project deleted",
    deleteFailed: "Failed to delete",
    confirmTitle: "Delete this project?",
    confirmBody:
      '"{{name}}" will be permanently removed, including all employees, timesheets, and documents. This cannot be undone.',
    tabProjects: "Projects",
    tabUsers: "Users",
    tabEntries: "Entries",
    tabEmail: "Email log",
    tabWeekNotes: "Week notes",
    tabLocks: "Locks",
    tabAudit: "Change history",
    tabSystemLogs: "System logs",
    tabSettings: "Settings",
    tabImports: "Imports",
    locksTitle: "Month locks",
    locksBody:
      "Pick a company and project, then lock a From–To interval or unlock it with a reason.",
    locksCompany: "Company",
    locksProject: "Project",
    locksPickCompany: "Select company",
    locksPickProject: "Select project",
    locksPickCompanyHint: "Select a company first.",
    locksPickProjectHint: "Select a project to view locks.",
    matrixNoHours_one:
      "{{count}} employee is assigned to this project but has not tracked any hours for {{year}} yet.",
    matrixNoHours_other:
      "{{count}} employees are assigned to this project but have not tracked any hours for {{year}} yet.",
    matrixDownloadXlsx: "Live-Tracking XLSX",
    matrixDownloadDone: "Download started.",
    weekNotesTitle: "Week descriptions",
    weekNotesPickProject: "Select a project.",
    weekNotesNoneForYear_one:
      "No week descriptions saved for {{year}} yet ({{count}} assigned employee).",
    weekNotesNoneForYear_other:
      "No week descriptions saved for {{year}} yet ({{count}} assigned employees).",
    weekNotesColWeek: "Week",
    weekNotesColEmployee: "Employee",
    weekNotesColDescription: "Description",
    weekNotesColAttachments: "Attachments",
    weekNotesColUpdated: "Updated",
    emailEmpty: "No emails logged yet.",
    emailTemplate: "Template",
    emailTemplateAll: "All templates",
    emailTemplateMagicLink: "Magic link",
    emailTemplateInvite: "Invite",
    emailTemplatePasswordReset: "Password reset",
    emailTemplateReminder: "Reminder",
    emailTo: "Recipient",
    emailRecipientPlaceholder: "Search by email…",
    emailStatus: "Status",
    emailError: "Error",
    emailCreated: "Time",
    emailQueued: "{{count}} queued",
    emailFailed: "{{count}} failed",
    emailDailyGuardPaused: "Daily guard paused — normal mail held ({{sent}}/{{cap}} in 24h)",
    emailDailyGuardOk: "Daily quota {{sent}}/{{cap}} (24h)",
    auditChainOk: "Hash chain verified",
    auditChainFail: "Hash chain broken",
    auditChainRows: "{{count}} entries",
    auditDownload: "Excel export",
    entriesAuditTitle: "Change history",
    auditPickProject: "Select a project.",
    auditPickProjectHint: "Select a project to view change history.",
    auditDay: "Day",
    auditOldNew: "Old → New",
    auditEmployee: "Employee",
    auditWho: "Who",
    auditOldNewHours: "h",
    auditOldNewAbsence: "absence",
    auditOldNewRd: "R&D",
    auditOldNewWeekNote: "Week description",
    auditEventsTitle: "System logs",
    auditEventsBody: "Application-level events (logins, invites, exports, tenancy).",
    auditWhen: "Time",
    auditAction: "Action",
    auditSource: "Source",
    auditBy: "Changed by",
    auditChannel: "Channel",
    auditReason: "Reason",
    auditHours: "h:",
    auditAbsence: "absence:",
    auditRd: "R&D:",
    auditWeekDescription: "Week description:",
    auditReasonLabel: "Reason:",
    auditEventType: "Event",
    auditTarget: "Target",
    auditIp: "IP",
    auditFromTo: "From → To",
    auditMethod: "Method",
    auditMeta: "Meta",
    auditMetaDetails: "Details",
    auditMetaDialogTitle: "{{event}} · {{when}}",
    auditMetaDialogBody: "Full meta object for this event.",
    auditEventsCsv: "CSV export",
    auditLoginMethod: {
      password: "password",
      magic_link: "magic link",
      invite: "invite",
      password_reset: "password reset",
      login: "magic link",
    },
    auditEvent: {
      login_succeeded: "Login",
      login_succeeded_with_method: "Login ({{method}})",
      login_failed: "Login failed",
      logout: "Logout",
      magic_link_issued: "Magic link sent",
      magic_link_denied: "Magic link denied",
      magic_link_redeemed: "Magic link redeemed",
      invite_sent: "Invite sent",
      invite_link_copied: "Invite link copied",
      password_reset_requested: "Password reset requested",
      password_set: "Password set",
      session_revoked: "Session revoked",
      rate_limited: "Rate limited",
      company_created: "Company created",
      company_updated: "Company updated",
      company_archived: "Company archived",
      company_restored: "Company restored",
      company_deleted: "Company deleted",
      project_created: "Project created",
      project_updated: "Project updated",
      project_archived: "Project archived",
      project_restored: "Project restored",
      project_deleted: "Project deleted",
      employee_added: "Employee added",
      employee_updated: "Employee updated",
      employee_archived: "Employee archived",
      employee_restored: "Employee restored",
      employee_role_changed: "Role changed",
      employee_email_changed: "Email changed",
      assignment_created: "Assignment created",
      assignment_updated: "Assignment updated",
      assignment_removed: "Assignment removed",
      employees_imported: "Employees imported",
      month_locked: "Month locked",
      month_unlocked: "Month unlocked",
      export_stundenzettel: "Timesheet export",
      export_year_matrix: "Year matrix export",
      export_admin_xlsx: "Change history export",
      export_admin_csv: "System logs export",
      export_attachments_zip: "Attachments export",
      reminder_settings_updated: "Reminders updated",
      reminder_sent: "Reminder sent",
      project_invites_sent: "Project invites sent",
      settings_updated: "Setting updated",
      hours_imported: "Hours imported",
      audit_chain_verified: "Hash chain verified",
      dev_persona_switched: "Persona switched",
    },
    systemLogsSearch: "Search",
    systemLogsSearchPlaceholder: "Event, email, target, IP…",
    systemLogsEventTypeAll: "All events",
    systemLogsFrom: "From",
    systemLogsTo: "To",
    systemLogsNoMatches: "No matches.",
    settingsSaved: "Setting saved.",
    settingsInvalidValue: "Please check the values and try again.",
    settingsUpdated: "Last updated",
    settingsEmailSectionTitle: "Email",
    settingsEmailSectionBody:
      "App-wide sender credentials and outbox throttle for invites and reminders.",
    smtpMailTitle: "SMTP sender",
    smtpMailBody:
      "Sender address and app password. Host/port stay in env (SMTP_HOST/PORT). Without stored credentials, SMTP_* from the environment still applies. The password is stored encrypted and never returned in plaintext.",
    smtpMailSender: "Sender email",
    smtpMailSenderPlaceholder: "noreply@grantonomy.de",
    smtpMailReplyTo: "Reply-To (optional)",
    smtpMailReplyToPlaceholder: "support@company.com",
    smtpMailPassword: "App password",
    smtpMailPasswordPlaceholder: "Google App Password",
    smtpMailPasswordKeep: "Leave blank to keep the existing password",
    smtpMailPasswordConfigured: "A password is on file. Enter a new one only to change it.",
    smtpMailPasswordHint: "Set once; re-enter only when rotating.",
    emailOutboxTitle: "Outbox throttle",
    emailOutboxBody:
      "Limits how fast the worker drains the email queue. Defaults: 30/min, 1800/day, poll every 2s.",
    emailOutboxMaxPerMinute: "Max sends per minute",
    emailOutboxMaxPerMinuteHint: "Overall send rate across all mail types.",
    emailOutboxDailyCap: "Daily normal-mail cap",
    emailOutboxDailyCapHint: "Rolling 24h pause for non-urgent mail when reached.",
    emailOutboxWorkerInterval: "Worker interval (ms)",
    emailOutboxWorkerIntervalHint: "How often the outbox worker wakes (milliseconds).",
    workProfileLimitsTitle: "Work profile limits",
    workProfileLimitsBody:
      "Maximum hours and days per week when creating or editing employees. Weekly hours cannot exceed days/week × max hours per workday.",
    workProfileMaxHours: "Max hours/week",
    workProfileMaxDays: "Max days/week",
    workProfileMaxHoursPerDay: "Max hours/workday",
    workProfileMaxHoursPerDayHint:
      "Caps Stunden/Woche as days × this value (default 10). Absolute weekly max still applies.",
    rdShareLimitsTitle: "R&D share limits",
    rdShareLimitsBody:
      "Allowed per-assignment R&D % band. The max also caps the sum of active shares for one person.",
    rdShareMinPercent: "Minimum %",
    rdShareMaxPercent: "Maximum %",
    rdShareLimitsHint: "Integers from 1 to 100; minimum must not exceed maximum.",
    rateLimitsSectionTitle: "Rate limits",
    rateLimitsSectionBody:
      "Login and magic-link attempt budgets per IP (in-memory, single replica).",
    loginRateLimitTitle: "Login rate limit",
    magicLinkRateLimitTitle: "Magic-link rate limit",
    rateLimitAttempts: "Attempts",
    rateLimitAttemptsHint: "Allowed tries inside the window before lockout.",
    rateLimitWindow: "Window (seconds)",
    rateLimitWindowHint: "Rolling window length in seconds (e.g. 900 = 15 min).",
    rateLimitLockout: "Lockout (seconds)",
    rateLimitLockoutHint: "Cooldown after the budget is exhausted (0 = none).",
    authTokenTtlTitle: "Auth token lifetimes",
    authTokenTtlBody:
      "How long one-time tokens stay valid (seconds). Defaults: login 3d, invite 7d, password reset 30 min.",
    authTokenTtlLogin: "Login / magic-link token (seconds)",
    authTokenTtlLoginHint: "259200 = 72 hours.",
    authTokenTtlInvite: "Invite token (seconds)",
    authTokenTtlInviteHint: "604800 = 7 days.",
    authTokenTtlPasswordReset: "Password-reset token (seconds)",
    authTokenTtlPasswordResetHint: "1800 = 30 minutes.",
    settingsLegalSectionTitle: "Legal",
    settingsLegalSectionBody:
      "Privacy Policy and Terms appear on /privacy and /terms and in invitation emails. Markdown is stored as formatted text.",
    legalPrivacyTitle: "Privacy Policy",
    legalPrivacyBody: "Public privacy policy (German and English).",
    legalTermsTitle: "Terms and Conditions",
    legalTermsBody: "Public terms of use (German and English).",
    legalEditorFormatted: "Formatted",
    legalEditorRaw: "Raw",
    legalEditorRawHint:
      "Raw markdown is stored as typed. The formatted editor may rewrite syntax it does not support.",
    importsEmpty: "No imports yet.",
    importsKind: "Kind",
    importsCompany: "Company",
    importsProject: "Project",
    importsFilename: "File",
    importsDryRun: "Dry run",
    importsStats: "Stats",
    importsCreated: "Time",
    hoursImportTitle: "Import historical hours",
    hoursImportBody:
      "Dry-run with per-person/month reconciliation. Template matches the Live/calendar export (email, project, date, hours, R&D).",
    hoursImportCompany: "Company",
    hoursImportProject: "Project (optional)",
    hoursImportPickCompany: "Choose a company",
    hoursImportAllProjects: "All projects (use “Projekt” column)",
    hoursImportTemplate: "Download template",
    hoursImportUpload: "Check file",
    hoursImportPreviewDone: "Preview ready.",
    hoursImportTotals: "{{accepted}} rows ok, {{rejected}} rejected · Σ {{total}} h / {{rd}} h R&D",
    hoursImportRow: "Row",
    hoursImportReason: "Reason",
    hoursImportEmail: "Email",
    hoursImportCommit: "Commit import",
    hoursImportCommitted:
      "Import done: {{inserted}} inserted, {{updated}} updated, {{unchanged}} unchanged.",
  },
  wizard: {
    step1Title: "Project",
    step1Desc: "Info & documents",
    step2Title: "Employees",
    step2Desc: "Data & allocation",
    step3Title: "Generate",
    step3Desc: "Timesheets",
    step4Title: "Validate",
    step4Desc: "Compliance checks",
    step5Title: "Export",
    step5Desc: "Download",
  },
  dropzone: {
    reading: "Reading files…",
    dragActive: "Drop them here",
    idle: "Drop files or click to upload",
    removeAria: "Remove",
  },
  festschreibung: {
    metaTitle: "Month locks — Grantonomy",
    title: "Month locks",
    body: "Select a project, then lock a From–To interval. Unlocking is Grantonomy Admin only.",
    project: "Project",
    pickProject: "Select project",
    pickProjectHint: "Select a project to view locks.",
  },
  legal: {
    metaTitle: "Privacy Policy — Grantonomy",
    privacyMetaTitle: "Privacy Policy — Grantonomy",
    termsMetaTitle: "Terms and Conditions — Grantonomy",
    kicker: "Legal",
    title: "Privacy Policy",
    subtitle: "How we process your data.",
    linkPrivacy: "Privacy",
    linkTerms: "Terms",
    continueNotice:
      "By continuing you accept the <0>Terms and Conditions</0> and acknowledge the <1>Privacy Policy</1>.",
    localeDe: "Deutsch",
    localeEn: "English",
    emptyPrivacy: "No privacy policy has been published yet.",
    emptyTerms: "No terms have been published yet.",
  },
  notFound: {
    metaTitle: "Page not found — Grantonomy",
    title: "Page not found",
    body: "This page doesn't exist or has been moved.",
    home: "Go home",
  },
  errorBoundary: {
    title: "This page didn't load",
    body: "Something went wrong. Try refreshing or head back home.",
    retry: "Try again",
    home: "Go home",
  },
  appShell: {
    openMenu: "Open menu",
    closeMenu: "Close menu",
    mobileNavTitle: "Navigation",
    nav: {
      employees: "Employees",
      myTracking: "My tracking",
      festschreibung: "Month locks",
    },
    role: {
      grantonomyAdmin: "Grantonomy Admin",
      customerAdmin: "CompanyAdmin",
      grantonomy_admin: "Grantonomy Admin",
      company_admin: "CompanyAdmin",
      employee: "Employee",
      signedIn: "Signed in",
    },
  },
  roleSwitcher: {
    label: "Test: role",
    hint: "Test phase only — switches to a real seeded user for the chosen role",
    roles: {
      grantonomy_admin: "GrantonomyAdmin",
      company_admin: "CompanyAdmin",
      employee: "Employee",
    },
  },
  audit: {
    nav: "Audit Assistant",
    kicker: "Audit Assistant",
    title: "Plausibility check like the tax office",
    subtitle:
      "AI-powered review of timesheets for {{project}} — R&D share per role, patterns, outliers and typical BSFZ audit questions.",
    runCheck: "Run check",
    rerun: "Run again",
    running: "Checking…",
    runningLong: "AI is analysing timesheets, roles and R&D shares — this can take up to a minute.",
    backToProject: "Back to project",
    emptyTitle: "No audit run yet",
    emptyBody:
      "Start an AI-powered plausibility check. We assess R&D shares per role, unrealistic patterns and the questions a Finanzamt auditor would typically ask.",
    findings: "Findings",
    noFindings: "No issues found. Looks clean.",
    overall: {
      ok: "All plausible",
      warning: "Minor findings",
      critical: "Critical findings",
    },
  },
  assessment: {
    kicker: "Audit Assessment",
    title: "Audit dialog with the tax office",
    subtitle:
      "Critical all-in audit assessment for {{project}} — roles, R&D hours, BSFZ decision and project content under scrutiny.",
    backToProject: "Back to project",
    opening: "The tax office is reviewing the file…",
    thinking: "Tax office is typing…",
    placeholder: "Your explanation to the tax office…",
    hint: "Cmd/Ctrl + Enter to send",
    exportMarkdown: "Markdown",
    exportPdf: "PDF",
    restart: "Restart",
    roleUser: "You",
    roleAuditor: "Tax office",
    roleAuditorFull: "Tax office (auditor)",
    popupBlocked: "Popup blocked",
    printSaveAsPdf: "Save as PDF",
    pdfKicker: "Audit Assessment — R&D Tax Credit",
    pdfCreated: "Created on {{date}}",
    pdfFooter: "Generated by Grantonomy · {{date}}",
    failed: "Assessment failed",
  },
  brand: {
    tagline: "Time Tracker",
  },
  employeeLogin: {
    title: "Employee login",
    intro:
      "Enter your email — we'll send you a login link. Your access is tied to your account and never lost.",
    sentTitle: "Link sent",
    sentBody: "We sent an email to <b>{{email}}</b>. Open the link inside to sign in.",
    useOtherEmail: "Use a different email",
    emailLabel: "Email address",
    submit: "Send login link",
    submitting: "Sending …",
    footnote: "Only email addresses registered as an employee on a project can sign in.",
    genericError: "Error",
    metaTitle: "Employee login — Grantonomy",
  },
  forgotPassword: {
    metaTitle: "Forgot password — Grantonomy",
    title: "Forgot your password.",
    subtitle: "We'll send you a link to choose a new password.",
    emailLabel: "Email address",
    submit: "Send reset link",
    submitting: "Sending …",
    sentTitle: "Check your email",
    sentBody:
      "Check your inbox. If an account exists for <b>{{email}}</b>, an email is on its way.",
    useOtherEmail: "Different email",
    backToLogin: "Back to sign in",
    genericError: "Error",
  },
  track: {
    metaTitle: "Sign-in required — Grantonomy",
    title: "Please sign in",
    body: "Personal tracking links have been replaced. Sign in with your email address — your access is tied to your account and can no longer be lost.",
    cta: "Go to employee login",
  },
  myTracking: {
    metaIndex: "My time entries — Grantonomy",
    metaProject: "Time tracking — Grantonomy",
    heading: "My projects",
    intro: "Pick a project to enter your hours.",
    loading: "Loading …",
    emptyProject: "You are not assigned to a project yet. Please contact your administrator.",
    year: "Year {{year}}",
    projectsBack: "Projects",
    noAccess: "No access to this project.",
    back: "Back",
  },
  tracker: {
    hoursHeading: "Time entries {{year}}",
    hello: "Hi {{name}}",
    projectPrefix: "Project: {{name}}",
    projectDash: "—",
    targetPerWeek: "Target: {{weekly}}h/week ({{perDay}}h/day)",
    rdShare: "R&D share: {{pct}}%",
    holidaysIn: "Holidays: {{state}}",
    excelExport: "Excel export",
    statTotal: "Total",
    statRd: "of which R&D",
    statVac: "Vacation",
    statSick: "Sick",
    statUnitHours: "h",
    statUnitDays: "days",
    missing: "missing",
    weeksShort: "wk",
    outsideWindowHint:
      "Outside the tracking window (before project start, before assignment date, or after project end).",
    outsideWindowBadge: "unavailable",
    monthHint: "Values are pre-filled. Click 🌴/🤒 to cycle between full day, half day and off.",
    absentElsewhere: "You were absent this day in another project.",
    noWorkdays: "No workdays in this month.",
    week: "Calendar week {{kw}}",
    weekSaved: "Week saved",
    weekPartial: "partially saved",
    colDay: "Day",
    colWorkday: "Workday",
    workdayLockedHasData: "This day already has tracked hours.",
    workdayLockedAssigned: "Assigned workday — cannot be unchecked.",
    workdayExtraNeedsHours: "Checked extra days need hours (R&D may be 0).",
    workdayLockedHoliday: "Holiday — workday cannot be changed.",
    workdayLockedFuture: "Future day — workday cannot be changed.",
    workdayLockedOutside: "Outside the tracking window — workday cannot be changed.",
    workdayLockedMonth: "Month locked — workday cannot be changed.",
    workdayLockedElsewhere: "Absence on another project — workday cannot be changed.",
    workdayLockedOtherProject: "Hours on another project — workday cannot be changed.",
    colHours: "Hours",
    /** Short FYI under Stunden when the person has multiple active projects. */
    hoursSharedFyi: "Shared day total",
    hoursSharedTooltip:
      "Hours (total) are shared across all your projects on this day. Changing them here also updates Hours on your other projects.",
    hoursSharedTooltipWithProjects:
      "Hours (total) are shared across all your projects on this day. Already recorded in: <projects></projects>. Changing them here also updates Hours on your other projects.",
    hoursSharedHintAria: "Note: hours already recorded on other projects",
    colRd: "of which R&D",
    colRdPlanned: "R&D (planned)",
    colRdActual: "R&D (actual)",
    colAbsence: "Absence",
    future: "future",
    vacation: "Vacation",
    sick: "Sick",
    halfVacation: "½ Vacation",
    halfSick: "½ Sick",
    other: "Other",
    halfOther: "½ Other",
    absenceClearsOtherProjectsTitle: "Remove hours on other projects?",
    absenceClearsOtherProjectsBody:
      "This day already has tracked hours ({{projects}}). A full-day absence sets hours to 0 on all listed projects and removes those entries.",
    absenceClearsOtherProjectsConfirm: "Set absence",
    descLabel: "What did you work on this week?",
    descPlaceholder: "Short description of your activities…",
    removeAria: "Remove",
    uploading: "Uploading…",
    attachFile: "Attach file",
    attachmentsCount: "{{count}} / {{max}}",
    attachmentsEmpty: "No files attached yet.",
    attachmentDownload: "Download",
    attachmentDelete: "Delete",
    attachmentDeleteConfirm: "Delete this file?",
    attachmentCapReached: "Maximum {{max}} files per week.",
    attachmentsZipExport: "Attachments (ZIP)",
    attachmentsZipDone: "ZIP download started.",
    attachmentsZipFailed: "ZIP export failed.",
    attachmentsZipEmpty: "No attachments for {{year}}.",
    fileDeleteConfirm: "Really delete this file?",
    fileUploaded: "File uploaded",
    filesUploaded: "{{count}} files uploaded",
    uploadFailed: "Upload failed",
    genericError: "Error",
    saveAgain: "Save week again",
    saveWeek: "Save week",
    weekNothingEntered:
      "Please fill in all the active days (rows) by indicating your Actual R&D Hours or indicating the absence reason",
    weekAllZero: "A week with all zero hours cannot be saved.",
    weekZeroHoursRequireAbsence: "On days with 0 hours, please choose an absence reason.",
    weekActualRequired: "Please enter R&D (actual) for every day (including 0).",
    hoursInvalidNumber: "Please enter a valid number.",
    hoursOverCap: "Maximum {{cap}} hours per day.",
    rdOverTotal: "R&D cannot exceed total hours.",
    weekHoursInvalid: "Please fix invalid hours ({{dates}}).",
    futureWeekNotSaveable: "Future weeks cannot be saved yet.",
    reasonTitle: "Reason for change",
    reasonBody:
      "This week was already saved. Please provide a reason for the change — it is stored audit-proof in the log.",
    reasonPlaceholder:
      "e.g. Corrected R&D hours on Wednesday after discussion with the project lead",
    reasonTooShort: "Please provide a reason (min. 3 characters)",
    weekSavedToast: "Week {{kw}} saved",
    weekNotesSheet: "Week notes",
    xlsxCol: {
      date: "Date",
      weekday: "Weekday",
      kw: "Week",
      hours: "Hours",
      rd: "R&D hours",
      absence: "Absence",
      fraction: "Fraction",
      holiday: "Holiday",
      note: "Note",
      workedOn: "Worked on",
      docsCount: "# Documents",
      docs: "Documents",
      adminName: "Admin (name)",
      adminEmail: "Admin (email)",
      adminEditedAt: "Admin edited at",
    },
  },
  projectOverview: {
    metaTitle: "Project — Grantonomy",
    kicker: "Project",
    loading: "Loading…",
    backToDashboard: "Back to dashboard",
    audit: "Audit",
    editProject: "Edit project",
    moduleLabel: "Module {{n}}",
    reconstructTitle: "Reconstruction",
    reconstructDesc: "Import employees via Excel. Then generate, validate and export timesheets.",
    reconstructCta: "Continue →",
    liveTitle: "Live tracking",
    liveDesc:
      "Upload an Excel with employee data (name, email) — employees are created automatically and tracked live.",
    liveCta: "Open live tracking →",
    skippedYears: "Skipped years: {{years}}",
    noYearForModule: "No year configured for this module.",
  },
  projectsNew: {
    metaTitle: "New project — Grantonomy",
    uploadTitle: "Upload BSFZ approval",
    uploadSubtitle:
      "Upload the BSFZ approval — the AI extracts everything. Then choose per year between reconstruction and live tracking.",
    editSubtitle: "Edit and save the project master data.",
    forbidden: "Only Grantonomy admins can create new projects.",
  },
  dashboardEmployee: {
    kicker: "Dashboard",
    heading: "My projects",
    intro: "Pick a project to open your timesheet.",
    emptyBody: "You are not assigned to a project yet. Please contact your administrator.",
  },
  companiesExtra: {
    showArchived: "Show archived",
    archivedBadge: "archived",
    utilizationOverview: "Utilization & overview",
    archived: "Company archived",
    restored: "Company restored",
    hardDeleted: "Company permanently deleted",
    archiveTitle: "Archive company?",
    archiveBody:
      '"{{name}}" and all related projects will be hidden. Data and audit history are kept (GoBD). Can be restored at any time.',
    archiveConfirm: "Archive",
    restoreTitle: "Restore",
    hardDeleteTitle: "Delete permanently?",
    hardDeleteBody:
      '"{{name}}" will be removed together with <strong>all projects, time entries, week notes, attachments, locks and the audit history</strong>. This action cannot be undone.',
    hardDeleteConfirm: "Delete permanently",
    hardDeleteBodyPlain:
      '"{{name}}" will be removed together with all projects, assignments and the audit history. This is only possible while nothing has been tracked.',
  },
  adminExtra: {
    metaTitle: "Admin — All projects",
    showArchived: "Show archived",
    archivedBadge: "archived",
    archived: "Archived",
    restored: "Restored",
    hardDeleted: "Permanently deleted",
    restoreTitle: "Restore",
    archiveTitle: "Archive",
    hardDeleteTitle: "Delete permanently",
    archiveDialogTitle: "Archive project?",
    archiveDialogBody:
      '"{{name}}" will be hidden. Time entries and audit history are kept (GoBD). Can be restored at any time.',
    archiveDialogConfirm: "Archive",
    hardDeleteDialogTitle: "Delete permanently?",
    hardDeleteDialogBody:
      '"{{name}}" will be removed together with <strong>all time entries, week notes, attachments, locks and the audit history</strong>. This action cannot be undone.',
    hardDeleteDialogConfirm: "Delete permanently",
    genericError: "Error",
    colCompany: "Company",
    colRole: "Role",
    colEnabled: "Enabled",
    colLastLogin: "Last login",
    colLastEntry: "Last entry",
    colCreatedAt: "Created",
    usersEmpty: "No users found.",
    disable: "Disable",
    enable: "Enable",
    enabled: "Yes",
    disabled: "No",
    userEnabled: "User enabled.",
    userDisabled: "User disabled.",
    magicLink: "Magic link",
    passwordReset: "Password reset",
    magicLinkSent: "Magic link sent.",
    passwordResetSent: "Password reset sent.",
    selectProject: "Select project",
    year: "Year",
    projectTerm: "Project term: {{from}} – {{to}}",
    projectTermOpen: "Project term: from {{from}} (no end date)",
    termRange: "from {{from}} to {{to}}",
    termOpen: "since {{from}} with no end date",
    yearAdjusted:
      "“{{project}}” runs {{term}} — {{requested}} is outside the term, showing {{shown}} instead.",
    noAssignees:
      "No employees are assigned to this project yet — without an assignment nobody can track time.",
    statusFull: "Full (≥85%)",
    statusPartial: "Partial",
    statusEmpty: "Empty",
    communicationTitle: "Communication",
    communicationIntro:
      "Invitations and reminders for employees on this project. The sender mailbox is configured app-wide under Admin → Settings (SMTP).",
    placeholderHelp: "Placeholders:",
    placeholderHelpDetail:
      "Every invitation email always includes a “Zugang einrichten” button and a fallback link at the end of the body.",
    copyInvitationLink: "Copy invitation link",
    inviteLinkCopied: "Invitation link copied.",
    inviteLinkCopyFailed: "Could not copy the link.",
    logKindReminder: "Reminder",
    logKindInvite: "Invitation",
    inviteTitle: "Invitation to time tracking",
    inviteIntro: "Personalised first email with magic link — individually or as bulk send.",
    inviteSubject: "Subject",
    inviteBody: "Text",
    saveTemplate: "Save template",
    templateSaved: "Templates saved.",
    inviteSendBulk: "Send to {{count}}",
    inviteConfirmTitle: "Send invitation to {{count}} employees?",
    inviteConfirmBody: "Each person receives the personalised text and their own magic link.",
    inviteSent: "{{count}} invitation(s) sent.",
    reminderSubject: "Subject",
    reminderBody: "Text",
    recipientsPlain: "Recipients",
    selectionEmpty: "Selection empty",
    autoReminderTitle: "Automatic reminder",
    autoReminderIntro:
      "Every scheduled weekday to all employees who still have incomplete weeks for {{month}}.",
    reminderWeekday: "Weekday",
    reminderHour: "Hour",
    reminderActive: "Enable automatic sending",
    reminderActiveHint: "Runs every {{weekday}} at {{hour}}:00.",
    saveSettings: "Save settings",
    recipientsTitle: "Recipients ({{count}}) — {{missing}} missing",
    recipientsIntro:
      "For {{month}}. Automatic sending only goes to {{missing}} person(s) with incomplete weeks. You can trigger the reminder manually now.",
    recipientsNone: "Nobody is missing for {{month}} — everyone has entered their past workdays.",
    selectAll: "All",
    selectMissing: "Only missing",
    selectNone: "None",
    selectPerson: "Select {{name}}",
    nextRunDisabled: "Automatic sending is disabled.",
    nextRunToday: "Automatic sending runs today.",
    nextRunIn: "Next automatic send in {{days}} day(s)",
    alreadySent: "Already sent on {{when}}",
    notYetSent: "Not yet sent for {{month}}",
    badgeMissing: "Missing",
    badgeEntered: "Entered",
    noEmail: "no email",
    lastSent: "Last {{when}}",
    sendIndividually: "Send individually",
    selectedCount: "{{count}} selected",
    sendSelection: "Send to selection ({{count}})",
    sendMissing: "Send to all missing ({{count}})",
    reminderSent: "{{count}} reminder(s) sent.",
    reminderLog: "Send log",
    reminderLogEmpty: "No invitations or reminders sent yet.",
    reminderLogKind: "Type",
    weekday1: "Monday",
    weekday2: "Tuesday",
    weekday3: "Wednesday",
    weekday4: "Thursday",
    weekday5: "Friday",
    weekday6: "Saturday",
    weekday7: "Sunday",
    settingsSaved: "Reminder settings saved.",
  },
  companyDetail: {
    metaTitle: "Company — Grantonomy",
    kicker: "Company",
    deleteCompany: "Delete",
    deleteConfirm: "Delete company and all related projects, employees and timesheets?",
    deleted: "Company deleted",
    deleteFailed: "Failed to delete",
    tabEmployees: "Employees",
    tabProjects: "Projects",
    tabUtilization: "Utilization",
    peopleCount_one: "{{count}} person in this company",
    peopleCount_other: "{{count}} people in this company",
    empIntro: "Employees of this company are available to all projects.",
    empIntroEmpty: "People are useful after a first project exists. Create a project first.",
    add: "Add",
    firstName: "First name",
    lastName: "Last name",
    role: "Role",
    weeklyHours: "Weekly hours",
    empAdded: "Employee added",
    empEmpty: "No employees yet. Add employees or import them via the project creation flow.",
    projectsBadge_one: "{{count}} project",
    projectsBadge_other: "{{count}} projects",
    perWeek: "h/week",
    vacationShort: "Vacation",
    sickShort: "Sick",
    empDeleteMulti: "Delete employee from all {{count}} projects?",
    empDeleteOne: "Delete employee?",
    projIntro: "All R&D projects of this company, grouped by fiscal year.",
    newProject: "New project",
    projEmpty: "No projects yet for this company.",
    noYear: "No year",
    projectsWord_one: "project",
    projectsWord_other: "projects",
    utilIntro:
      "Per year: for each project the target R&D share (%) from the project data and below it the actually tracked R&D share (Actual % = R&D hours ÷ total tracked hours for the year). On the right Σ Target and Σ Actual across all projects. If > 100% Target is booked in a year, the row is flagged as <mark>Overbooked</mark>.",
    utilNoProjects: "No projects.",
    colEmployee: "Employee",
    colTargetActual: "Target % · Actual %",
    colStatus: "Status",
    utilNoEmp: "No employees in this year.",
    statusOver: "Overbooked > 100%",
    statusFull: "Full",
    statusOk: "OK",
    capTitle: "R&D capacity & cross-project overbooking",
    capIntro:
      "Booked R&D hours across all projects vs. contractual capacity. Day cap = weekly hours ÷ workdays. 4h + 4h R&D on two projects = 100% OK, 6h + 6h = overbooked.",
    capEmpty: "No employees in this company yet — without people there is no capacity.",
    capNoHoursForYear: "No R&D hours tracked for {{year}} yet, so every capacity below reads 0%.",
    capNoProjects: "This company has no projects yet — only the current year is offered.",
    capNoConflicts: "No overbooked days — everyone stays within capacity.",
    capConflicts_one: "{{count}} overbooked day found",
    capConflicts_other: "{{count}} overbooked days found",
    capOverbooked: "Overbooked",
    capOverDays_one: "{{count}} day > 100%",
    capOverDays_other: "{{count}} days > 100%",
    capConflictDays: "Overbooked days",
    capOverLimitDays: "Days over the daily maximum",
    capOverLimitIntro:
      "R&D hours above the administrative daily maximum — a hard breach, not a soft contract overrun.",
    capBooked: "Booked",
    capRdBooked: "R&D booked",
    capPerProject: "R&D per project",
    capTotalHours: "Total hours",
    capCapacity: "Capacity",
    capRd: "R&D",
    capDay: "day",
    capPeriod: "Period",
    gran_week: "Week",
    gran_month: "Month",
    gran_year: "Year",
  },
  employees: {
    metaTitle: "Employees — Grantonomy",
    kicker: "Team",
    title: "Employees",
    intro:
      "Everyone at this company. How long someone has been on a project follows from the assignment's project entry date and optional project leave date — there is no year filter.",
    identifierHint:
      "The email address is the unique identifier and can only be changed later by Grantonomy.",
    backToProject: "Back to project",
    backToCompany: "Back to company",
    chooseCompany: "Choose a company",
    chooseCompanyBody: "Pick a company first to see its people.",
    search: "Search",
    searchPlaceholder: "Name or email",
    showArchived: "Show archived",
    empty: "No employees yet. Add someone or import a spreadsheet.",
    noMatches: "No matches.",
    colName: "Name",
    colPosition: "Position",
    colEmail: "Email",
    colHours: "Hours/week",
    colHoursLine1: "Hours",
    colHoursLine2: "week",
    colDays: "Days/week",
    colDaysLine1: "Days",
    colDaysLine2: "week",
    colProjects: "Projects",
    colActions: "Actions",
    roleEmployee: "Employee",
    roleCompanyAdmin: "CompanyAdmin",
    archivedBadge: "archived",
    noAssignments: "No assignment",
    add: "Add employee",
    addTitle: "Add employee",
    addBody: "Create the person and put them on a project straight away.",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    position: "Position",
    positionPlaceholder: "e.g. Research engineer",
    jobDescription: "Job description",
    jobDescriptionPlaceholder: "e.g. Planning and conducting research projects …",
    jobDescriptionHint: "{{count}} characters left",
    weeklyHours: "Hours/week",
    workdaysPerWeek: "Days/week",
    weeklyHoursCapHint:
      "Based on admin settings, the weekly maximum is {{max}} h. Hours/week and days/week are informational.",
    weeklyHoursOverWorkdayCap:
      "Hours/week cannot exceed {{max}} ({{days}} day(s) × {{perDay}} h per workday).",
    workWeekdays: "Working days",
    workWeekdaysHint: "Please pick {{count}} day(s) — currently {{selected}}.",
    role: "Role",
    sendInvite: "Send the invitation now",
    added: "Employee created",
    edit: "Edit",
    editTitle: "Edit employee",
    updated: "Employee updated",
    editBody: "Name, hours/week, working days and email.",
    emailChangeWarning:
      "The email address will change. Every open link and session on the old address stops working.",
    archive: "Archive",
    archiveTitle: "Archive this person?",
    archiveBody:
      "“{{name}}” disappears from the list and can no longer sign in. Tracked hours are kept (GoBD) and the person can be restored at any time.",
    archived: "Person archived",
    restore: "Restore",
    restored: "Person restored",
    promote: "Make company admin",
    demote: "Make employee",
    roleChanged: "Role changed",
    invite: "Send invitation",
    inviteAgain: "Send invitation again",
    invited: "Invitation sent",
    inviteMany: "Send invitation ({{count}})",
    inviteResult: "{{sent}} sent · {{skipped}} skipped",
    neverSignedIn: "Never signed in",
    neverSignedInInvited: "Never signed in • Invited {{count}}",
    assign: "Assign to project",
    assignTitle: "Assign to project",
    assignBody: "Project, project entry date and R&D share for {{name}}.",
    assignProject: "Project",
    assignEntryDate: "Project Entry Date",
    assignLeaveDate: "Project Leave Date",
    assignLeaveDateHint: "Last trackable day on this project; later months are not editable.",
    assignRdShare: "R&D share ({{min}}–{{max}}%)",
    assignRdShareColumnHeader: "R&D share (%)",
    assignRdRemaining: "Maximum value: {{max}}%",
    assignRdRemainingHint: "Based on this person’s other project assignments",
    assigned: "Assigned to the project",
    editAssignment: "Edit assignment",
    editAssignmentBody: "Project entry date and R&D share are Grantonomy's to change.",
    assignmentUpdated: "Assignment updated",
    removeAssignment: "Remove assignment",
    removeAssignmentTitle: "Remove this assignment?",
    removeAssignmentBody: "“{{name}}” will be taken off “{{project}}”.",
    assignmentRemoved: "Assignment removed",
    assignmentArchived: "Assignment archived — the tracked hours are kept.",
    lockedTooltip: "Contains tracked hours — only Grantonomy can change this.",
    noProjectsYet: "This company has no project yet.",
    import: "Excel import",
    importTitle: "Employees from Excel",
    importBody:
      "The “Grantonomy Timesheet Input – Live Tracking” template: name, email, weekly hours, days per week and R&D share. The import creates people and assigns them to “{{project}}”.",
    importProject: "Target project",
    importEntryDate: "Default project entry date",
    importEntryDateHint: "Used for rows that carry no project entry date of their own.",
    importLeaveDateHint:
      "Project leave date from Excel applies only to new people. For someone who already exists, set it when editing the assignment.",
    importTemplate: "Download the template",
    importDrop: "Drop an Excel file (.xlsx) here, or click",
    importParsing: "Reading the file…",
    importPreview: "{{count}} rows detected",
    importCommit: "Create {{count}} from Excel",
    importSummary: "{{created}} new · {{assigned}} assigned · {{skipped}} skipped",
    importColRow: "Excel row",
    importExcelRow: "Excel row {{n}}",
    importAdminFallback: "We consider {{name}} as company admin. You can change this later.",
    importColVerdict: "Result",
    verdict_create: "Create",
    verdict_assign: "Assign",
    verdict_skip: "Skipped",
    importDiffers: "Master data differs: {{fields}} — left untouched.",
    reason_duplicate_in_file: "Duplicate row in the file",
    reason_belongs_to_other_company: "A user with this email is assigned to another company",
    reason_already_assigned: "Already on this project",
    reason_person_archived: "The person is archived",
    reason_assignment_has_entries: "Contains tracked hours — only Grantonomy can change this",
    reason_first_user_must_be_company_admin: "The company needs a company admin first",
    reason_missing_email: "No email address",
    reason_job_description_too_long: "Job description too long (max 2000 characters)",
    reason_missing_weekly_hours: "No weekly hours",
    reason_weekly_hours_over_max: "Weekly hours exceed the allowed maximum",
    reason_weekly_hours_exceed_workday_cap: "Weekly hours exceed days × max hours per workday",
    reason_missing_workdays: "No working days per week",
    reason_missing_rd_share: "No R&D share",
    reason_rd_share_out_of_range: "R&D share is outside the allowed range",
    reason_rd_share_exceeds_max: "R&D shares across projects would exceed the configured maximum",
    reason_invalid_entry_date: "Project entry date cannot be read",
    reason_entry_before_project_start: "Project entry date is before the project start",
    reason_entry_after_project_end: "Project entry date is after the project end",
    reason_entry_too_far_in_future: "Project entry date is too far in the future",
    reason_invalid_leave_date: "Project leave date cannot be read",
    reason_assignment_leave_before_entry: "Project leave date is before the project entry date",
    reason_assignment_leave_after_project_end: "Project leave date is after the project end",
    reason_assignment_leave_before_project_start: "Project leave date is before the project start",
    reason_empty_workbook: "The file contains no sheet",
  },
  projectsList: {
    metaTitle: "Projects — Grantonomy",
    kicker: "Company",
    title: "Projects",
    intro: "A project runs for its whole Laufzeit — the year is only a view.",
    chooseCompanyIntro:
      "Pick a company first to see its projects. New companies are created from New project.",
    newProject: "New project",
    showArchived: "Show archived",
    empty: "No projects yet.",
    yearFilter: "Year",
    allYears: "All years",
    allCompanies: "All companies",
    selectCompany: "Show projects",
    manageCompany: "Manage company",
    backToCompanies: "All companies",
    searchCompanies: "Search companies",
    searchProjects: "Search projects",
    runtime: "Runtime",
    open: "Open ended",
    people_one: "{{count}} person",
    people_other: "{{count}} people",
    archivedBadge: "archived",
    toEmployees: "Company employees",
  },
  projectForm: {
    title: "New project",
    subtitle: "Project master data (name, term, and funding details).",
    companySection: "Company",
    companySectionHint: "Pick an existing company or add a new one right here.",
    company: "Company",
    newCompany: "Add a new company…",
    newCompanyHint: "Name and state for the new company — saved only when you create the project.",
    createCompany: "Create company",
    companyName: "Company name",
    infoSection: "Project info",
    infoSectionHint: "Review and edit. Required fields must be set before creating.",
    name: "Project title",
    namePlaceholder: "Shown in your dashboard",
    nameHint: "Displayed in the dashboard. Freely editable.",
    bsfzDescription: "BSFZ project description",
    bsfzDescriptionPlaceholder: "Short project name per BSFZ approval",
    bsfzDescriptionHint: "Used as the project title on the Stundenzettel.",
    projectCode: "Project ID (Lfd. Nr.)",
    projectCodePlaceholder: "666-969-422/2026-1",
    projectCodeHint: "Serial number from the BSFZ approval notice. Optional.",
    projectCodeInvalid:
      "Use the Lfd. Nr. shape, e.g. 666-969-422/2026-1 (three triplets / year-n).",
    startDate: "Project start",
    endDate: "Project end",
    submit: "Create project",
    created: "Project created",
    editTitle: "Edit project",
    saved: "Project saved",
    archive: "Archive",
    archived: "Project archived",
    restore: "Restore",
    restored: "Project restored",
    delete: "Delete",
    deleteDisabledHint:
      "Deletion is only possible while no hours have been tracked on this project.",
    deleteTitle: "Delete this project for good?",
    deleteBody:
      "“{{name}}” will be removed irreversibly. That is only possible while no hours have been tracked.",
    deleteConfirm: "DELETE",
    deleteConfirmHint: "Type DELETE to confirm.",
    deleted: "Project deleted",
    liveComingSoon: "Live tracking arrives in Phase 4.",
  },
  dashboardExtra: {
    backToDashboard: "Back to dashboard",
    projects_one: "project",
    projects_other: "projects",
    years_one: "year",
    years_other: "years",
  },
  scaffold: {
    title: "Work in progress",
    body: "This area is built in a later phase.",
  },
  liveStatus: {
    title: "Live capture status",
    subtitle: "{{month}} · sorted by capture gaps",
    loading: "Loading live status…",
    activeProjects: "Active projects",
    totalEmployees: "Employees total",
    monthEntered: "Month fully captured",
    monthEnteredHint_one: "{{count}} open",
    monthEnteredHint_other: "{{count}} open",
    monthEnteredAll: "all done",
    avgCompletion: "Ø capture rate",
    month: "Month",
    completion: "Completion",
    captureRate: "Capture rate",
    weeksMonth: "Weeks {{month}}",
    openLive: "Open",
    expand: "Details",
    collapse: "Collapse",
    empty: "No live projects with employees yet.",
    idleBadge: "No tracking",
    idleHint: "no employees in the current month",
    idleEmpty: "No employees assigned for this month — tracking is idle.",
    actualWeek: "Actual week:",
    actualMonth: "Actual month:",
    yearPct: "Year {{year}}",
    descriptionWeek: "Description week:",
    noEmployees: "No active employees this week.",
    yearTotal: "Σ Year",
    projectTotal: "Σ Project",
    tileTeamComplete: "Team complete",
    tileTeamCompleteHint: "Employees with all weeks captured",
    tileWeeksTracked: "Weeks captured",
    tileWeeksTrackedHint: "{{pct}} % in {{month}}",
    tileWithoutEntry: "Without entry",
    tileWithoutEntryHint: "Employees with no captured week",
    tileAvgRd: "Ø R&D share",
    tileAvgRdHint: "weighted by hours, month",
    tileLastCapture: "Last capture",
    lastCaptureToday: "today",
    lastCaptureYesterday: "1 day ago",
    lastCaptureDays: "{{count}} days ago",
    lastCaptureNone: "no capture",
    weekNoteYes: "Week ✓",
    weekNoteNo: "Week ·",
    lastEntry: "last {{date}}",
    lastEntryOn: "Last captured: {{date}}",
    noEntry: "no entry",
    weeks: "Weeks",
    weeksShort: "{{saved}}/{{total}} wk",
    weeksCell: "{{saved}}/{{total}} W",
    monthNa: "n/a",
    cellRdPct: "{{pct}} % R&D",
    monthTooltipHead: "{{name}} · {{month}}",
    monthTooltipNa: "{{name}} · {{month}}\nOutside project period (n/a)",
    monthTooltipWeeks: "Weeks captured: {{saved}} of {{total}}{{kw}}",
    monthTooltipHours: "Hours: {{hours}} h · of which R&D {{rd}} h ({{rdPct}} %)",
    monthTooltipNotes: "Descriptions: {{notes}} of {{saved}} weeks",
    monthTooltipLast: "Last capture: {{last}}",
    kwRange: "CW {{from}}–{{to}}",
    kwSingle: "CW {{week}}",
    kwNone: "CW —",
    legendGreen: "all weeks captured",
    legendYellow: "partly captured",
    legendRed: "no week captured",
    legendGrey: "outside project period",
    legendWeeks: "W = calendar weeks",
    notesIncomplete: "Descriptions incomplete",
    remind: "Reminder",
    remindHint: "Send reminder",
    remindConfirm: "Send email to {{name}} ({{email}})?",
    remindSend: "Send",
    rdPct: "R&D %",
    weeklyRagLabel: "Weeks captured: {{saved}} of {{total}}",
  },
  monthLock: {
    title: "Month lock",
    body: "Locked intervals cannot be edited — unless Grantonomy unlocks them with a reason.",
    lock: "Lock interval",
    unlock: "Unlock",
    unlockTitle: "Unlock interval",
    unlockBody:
      "Please provide a reason (min. 3 characters). The action is stored in the audit log.",
    reasonPlaceholder: "e.g. Correction after discussion with the client",
    reasonTooShort: "Please provide a reason (min. 3 characters)",
    lockedToast: "{{fromMonth}}–{{toMonth}}/{{year}} locked",
    unlockedToast: "{{fromMonth}}–{{toMonth}}/{{year}} unlocked",
    lockedBadge: "locked",
    unlockedBadge: "unlocked",
    lockedHint: "This month is locked.",
    lockedMonthBody: "This month is locked and can no longer be edited",
    noLocks: "No locked intervals.",
    selectMonth: "Month",
    selectFromMonth: "From",
    selectToMonth: "To",
    selectYear: "Year",
    outsideTerm: "Only months inside the project term ({{term}}) can be locked.",
    entireProject: "(entire project)",
    lockedOn: "Locked on {{when}}",
    unlockedOn: "unlocked on {{when}} – reason: {{reason}}",
    caCannotUnlock: "Only Grantonomy can unlock months.",
  },
  adminEdit: {
    title: "Edit timesheet – {{name}}",
    description: "Admin mode for {{year}}. All changes are recorded in the audit log.",
    open: "Edit",
  },
  projectLive: {
    metaTitle: "Live tracking — Grantonomy",
    kicker: "Live tracking",
    title: "Live overview",
    overviewTitle: "Year overview {{year}}",
    overviewSubtitle: "Total hours (h) and R&D hours per month. Click a month to highlight it.",
    overviewHolidayHint: "Holidays per {{bundesland}}",
    timeframe: "Year {{start}} – {{end}}",
    timeframeSingle: "Year {{year}}",
    timeframeOpen: "Year {{start}} – Open ended",
    manageEmployees: "Manage employees",
    yearTotal: "Σ Year",
    yearTotalGroup: "Year total",
    colHours: "h",
    colRd: "R&D",
    colPct: "%",
    currentMonthLegend: "CURRENT MONTH",
    scrollHint: "Scroll horizontally to view full year",
    rdActualTarget: "R&D actual / target",
    action: "Action",
    outsideEmployment: "Outside employment period",
    emptyEmployees: "No employees yet.",
    weekNotesTitle: "Week descriptions",
    weekNotesSubtitle: "What employees entered for each week ({{year}}).",
    weekNotesEmpty: "No week descriptions yet for {{year}}.",
    weekNotesError: "Week descriptions could not be loaded.",
    back: "Back to projects",
    year: "Year",
    live: "Live",
    retrospective: "Retrospective",
    yearWithMode: "{{year}} · {{mode}}",
    stepsLabel: "Live module navigation",
    step2Title: "Employees",
    step3Title: "Overview",
    step4Title: "Communication",
    bulkTitle: "Excel template (bulk)",
    bulkBody:
      "“Grantonomy Timesheet Input – Live Tracking” with name, email, weekly hours and R&D share.",
    rosterTitle: "Employees ({{count}})",
    rosterEmptyHint: "Optional — or add an employee individually.",
    rosterHeading: "Employees on this project",
    employee: "Employee",
    hours: "Hours",
    rd: "R&D",
    weeks: "Weeks",
    empty: "No employees are assigned to this project yet.",
    loading: "Loading…",
    exportYearMatrix: "Year matrix (XLSX)",
    exportAttachmentsZip: "Attachments (ZIP)",
    exportStundenzettelZip: "Timesheet (ZIP)",
    exportStundenzettelZipDone: "Timesheet ZIP downloaded",
    exportStundenzettelZipFailed: "Could not download timesheet ZIP",
    exportDone: "Export ready.",
    exportFailed: "Export failed.",
    exportPerson: "Timesheet",
  },
  errors: {
    DAY_FUTURE_HOURS: "Working hours cannot be booked in the future.",
    DAY_BEFORE_ASSIGNMENT: "The date is before the project entry date ({{assignment_entry_date}}).",
    DAY_IS_HOLIDAY: "Hours cannot be booked on a public holiday ({{holiday}}).",
    ABSENCE_FULL_REQUIRES_ZERO: "A full-day absence leaves no room for working hours.",
    ABSENCE_INCONSISTENT: "Absence type and fraction have to match.",
    ABSENCE_BLOCKED_BY_OTHER_PROJECT_HOURS:
      "Selected day(s) already have tracked hours on another project, so they cannot be marked as absent.",
    ZERO_HOURS_REQUIRE_ABSENCE: "On days with 0 hours, please choose an absence reason.",
    DAILY_CAP_EXCEEDED: "These hours exceed the daily maximum ({{daily_cap}} h).",
    WEEKLY_HOURS_EXCEEDED:
      "Week hours ({{week_total}} h) exceed the contractual weekly hours ({{weekly_hours}} h).",
    ENTRY_FUTURE_HOURS: "R&D hours cannot be booked in the future.",
    ENTRY_BEFORE_ASSIGNMENT:
      "The date is before the project entry date ({{assignment_entry_date}}).",
    ENTRY_BEFORE_PROJECT_START: "The date is before the project start ({{project_start_date}}).",
    ENTRY_AFTER_PROJECT_END: "The date is after the project end ({{project_end_date}}).",
    ENTRY_AFTER_LEAVE: "The date is after the project leave date ({{assignment_leave_date}}).",
    YEAR_BEFORE_PROJECT_START: "{{year}} is before the project start ({{project_start_date}}).",
    YEAR_AFTER_PROJECT_END: "{{year}} is after the project end ({{project_end_date}}).",
    MONTH_BEFORE_PROJECT_START:
      "{{month}}/{{year}} is before the project start ({{project_start_date}}).",
    MONTH_AFTER_PROJECT_END: "{{month}}/{{year}} is after the project end ({{project_end_date}}).",
    DAY_RECORD_MISSING: "No total hours have been recorded for this day yet.",
    RD_EXCEEDS_TOTAL: "R&D hours cannot exceed the day's total ({{total_hours}} h).",
    CROSS_PROJECT_OVERBOOKING:
      "On {{date_label}}, R&D hours across all projects exceed the day's total ({{total_hours}} h).",
    CROSS_PROJECT_OVERBOOKING_hint: "Already recorded on {{date_label}}:",
    /** Option B toast title (cross-project overbook breakdown). */
    CROSS_PROJECT_OVERBOOKING_title:
      "On {{date_label}}, R&D ACTUAL on your other project(s) already fills the day:",
    CROSS_PROJECT_OVERBOOKING_line: "• {{name}} — {{hours}}h",
    CROSS_PROJECT_OVERBOOKING_more: "+{{count}} more",
    CROSS_PROJECT_OVERBOOKING_footer:
      "Day total (Hours) is {{total_hours}} h — lower R&D (actual) or raise Hours.",
    NO_ASSIGNMENT: "This person is not assigned to the project.",
    NO_WORK_PROFILE: "No weekly hours are configured for this person.",
    RETRO_REASON_REQUIRED: "Retroactive changes require a reason (at least 3 characters).",
    CHANGE_REASON_REQUIRED:
      "Changes to already-saved hours require a reason (at least 3 characters).",
    MONTH_LOCKED:
      "Month {{month}}/{{year}} is locked (project “{{project_name}}”). Please unlock it with a reason first.",
    AUDIT_APPEND_ONLY: "The audit log is append-only and cannot be modified.",
    ASSIGNMENT_COMPANY_MISMATCH: "The person and the project belong to different companies.",
    ASSIGNMENT_ENTRY_BEFORE_PROJECT_START:
      "Project entry date cannot be before the project start ({{project_start_date}}).",
    ASSIGNMENT_ENTRY_AFTER_PROJECT_END:
      "Project entry date cannot be after the project end ({{project_end_date}}).",
    ASSIGNMENT_ENTRY_TOO_FAR_IN_FUTURE:
      "Project entry date cannot be later than {{horizon_date}} (end of the month two months ahead).",
    ASSIGNMENT_LEAVE_BEFORE_ENTRY:
      "Project leave date cannot be before the project entry date ({{entry_date}}).",
    ASSIGNMENT_LEAVE_BEFORE_PROJECT_START:
      "Project leave date cannot be before the project start ({{project_start_date}}).",
    ASSIGNMENT_LEAVE_AFTER_PROJECT_END:
      "Project leave date cannot be after the project end ({{project_end_date}}).",
    FIRST_USER_MUST_BE_COMPANY_ADMIN: "The first person of a company has to be a company admin.",
    LAST_COMPANY_ADMIN_PROTECTED: "A company needs at least one active company admin.",
    EMAIL_IMMUTABLE:
      "The email address is the unique identifier and can only be changed by Grantonomy.",
    EMAIL_TAKEN: "This email address is already in use.",
    RD_SHARE_EXCEEDS_MAX:
      "R&D shares across all projects cannot exceed {{max}}% ({{remaining}}% still available).",
    RD_SHARE_OUT_OF_RANGE: "invalid R&D% - please enter a number between {{min}}% and {{max}}%",
    ASSIGNMENT_HAS_ENTRIES: "Contains tracked hours — only Grantonomy can change this.",
    ASSIGNMENT_HAS_ENTRIES_AFTER_LEAVE:
      "Tracked hours exist after the project leave date (latest entry {{latest_entry_date}}).",
    HAS_TRACKED_DATA: "Data has already been tracked. This can be archived, but not deleted.",
    UNAUTHENTICATED: "Please sign in.",
    INVALID_CREDENTIALS: "Email address or password is incorrect.",
    ACCOUNT_DISABLED: "This account is disabled. Please contact Grantonomy.",
    PASSWORD_SETUP_REQUIRED: "Please choose a password first.",
    WEAK_PASSWORD: "The password needs at least {{min_length}} characters.",
    FORBIDDEN: "You do not have permission to do that.",
    TURNSTILE_FAILED: "Verification failed. Please try again.",
    NOT_FOUND: "Not found.",
    RATE_LIMITED: "Too many attempts. Please try again in {{seconds}} seconds.",
    RATE_LIMITED_LOGIN:
      "Too many sign-in attempts. At most {{attempts}} tries every {{window}}. Try again in {{countdown}}.",
    RATE_LIMITED_EMAIL:
      "Too many email requests. At most {{attempts}} emails every {{window}}. You can request another email in {{countdown}}.",
    TOKEN_INVALID: "This link is not valid.",
    TOKEN_EXPIRED: "This link has expired.",
    TOKEN_USED: "This link has already been used.",
    INVALID_INPUT: "The input is incomplete or invalid.",
    CONFLICT: "The record was changed in the meantime.",
    ALREADY_ASSIGNED: "Already assigned to this project.",
    CONSTRAINT_VIOLATED: "The input violates a data rule.",
    REFERENCE_NOT_FOUND: "The referenced record does not exist.",
    MISSING_ACTOR_CONTEXT: "The change could not be attributed. Please try again.",
    ATTACHMENT_TOO_LARGE: "The file is too large (max. 10 MB).",
    ATTACHMENT_TYPE_REJECTED: "This file type is not allowed.",
    ATTACHMENT_CAP_EXCEEDED: "Maximum 4 files per week.",
    ATTACHMENT_QUOTA_EXCEEDED: "The company storage quota is exhausted.",
    ATTACHMENT_ZIP_TOO_LARGE: "The ZIP export exceeds 300 MB — please reduce the year or scope.",
    STUNDENZETTEL_MINDESTANGABEN_MISSING:
      "The timesheet cannot be created. Project ID (Vorhaben-ID) and R&D activity (position) must be set.",
    INTERNAL_ERROR: "An unexpected error occurred.",
  },
};
